import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useUser, useSignIn } from '@clerk/react';
import { useAuth } from '../contexts/AuthProvider';
import { useT } from '../contexts/LocaleProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { VirgyWordmark } from '../components/VirgyMark';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { isLoaded } = useUser();
  const { signIn } = useSignIn();
  const { user: appUser, loading: authLoading } = useAuth();
  const t = useT();
  const [error, setError] = useState('');
  const [oauthLoading, setOauthLoading] = useState(false);

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    if (!signIn) return;
    setOauthLoading(true);
    setError('');
    try {
      const { error: ssoError } = await signIn.sso({
        strategy: 'oauth_google',
        redirectCallbackUrl: '/sso-callback',
        redirectUrl: '/',
      });
      if (ssoError) {
        setError(ssoError.message || t('login.failed'));
        setOauthLoading(false);
      }
    } catch (err) {
      console.error('OAuth error:', err);
      setError(t('login.failed'));
      setOauthLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!signIn || !email.trim()) return;
    setEmailLoading(true);
    setError('');

    const trimmed = email.trim();
    const { error: createError } = await signIn.create({ identifier: trimmed });
    if (createError) {
      const code = createError.code || '';
      if (code === 'form_identifier_not_found') {
        setError(t('login.emailNotAllowed'));
      } else {
        setError(createError.message || t('login.failed'));
      }
      setEmailLoading(false);
      return;
    }

    const { error: sendError } = await signIn.emailCode.sendCode({
      emailAddress: trimmed,
    });
    if (sendError) {
      setError(sendError.message || t('login.failed'));
      setEmailLoading(false);
      return;
    }

    setStep('code');
    setEmailLoading(false);
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!signIn || !code.trim()) return;
    setEmailLoading(true);
    setError('');

    const { error: verifyError } = await signIn.emailCode.verifyCode({
      code: code.trim(),
    });
    if (verifyError) {
      setError(verifyError.message || t('login.codeInvalid'));
      setEmailLoading(false);
      return;
    }

    const { error: finalizeError } = await signIn.finalize({
      navigate: () => navigate('/', { replace: true }),
    });
    if (finalizeError) {
      setError(finalizeError.message || t('login.failed'));
      setEmailLoading(false);
      return;
    }
    setEmailLoading(false);
  };

  const handleBackToEmail = async () => {
    if (signIn) {
      try { await signIn.reset(); } catch { /* noop */ }
    }
    setStep('email');
    setCode('');
    setError('');
  };

  useEffect(() => {
    if (appUser) navigate('/', { replace: true });
  }, [appUser, navigate]);

  if (authLoading || !isLoaded) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))] flex items-center justify-center text-sm text-[hsl(var(--text-soft))]">
        {t('common.loading')}
      </div>
    );
  }
  if (appUser) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[hsl(var(--bg-subtle))] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <VirgyWordmark size={32} />
        </div>

        <div className="bg-[hsl(var(--surface))] border border-border rounded-lg shadow-sm p-6">
          <h1 className="font-display text-xl text-center">{t('login.welcome')}</h1>
          <p className="mt-1 text-center text-sm text-[hsl(var(--text-soft))]">
            {t('login.subtitle')}
          </p>

          {error && (
            <div className="mt-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
              {error}
            </div>
          )}

          {step === 'email' && (
            <>
              <Button
                className="w-full mt-5"
                variant="outline"
                onClick={handleGoogleSignIn}
                disabled={!signIn || oauthLoading || emailLoading}
              >
                {oauthLoading
                  ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[hsl(var(--text))] mr-2" />
                  : <GoogleIcon />
                }
                {t('login.google')}
              </Button>

              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wide text-[hsl(var(--text-muted))]">
                  {t('login.or')}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-3">
                <Input
                  type="email"
                  autoComplete="email"
                  required
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={emailLoading || oauthLoading}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!signIn || emailLoading || oauthLoading || !email.trim()}
                >
                  {emailLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  )}
                  {t('login.emailContinue')}
                </Button>
              </form>
            </>
          )}

          {step === 'code' && (
            <form onSubmit={handleCodeSubmit} className="mt-5 space-y-3">
              <p className="text-sm text-[hsl(var(--text-soft))]">
                {t('login.codeSent', { email })}
              </p>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
                placeholder={t('login.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={emailLoading}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={!signIn || emailLoading || !code.trim()}
              >
                {emailLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                )}
                {t('login.codeVerify')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBackToEmail}
                disabled={emailLoading}
              >
                {t('login.codeBack')}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-[hsl(var(--text-muted))]">
          {t('login.footnote')}
        </p>
      </div>
    </div>
  );
};

export default Login;
